import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";

import MainView from "./MainView";
import InvitationItem from "@/features/invitations/components/molecules/InvitationItem";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import {
  InvitationsInvitationFragment,
  InvitationStatus,
  useInvitationsListMyOrganiationsSentInvitationsQuery,
} from "@/generated/graphql";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { ifWeb } from "@/utils/platform";

/**
 * NativeWind `md:` breakpoint (768px). The content panel fixes its width at md+
 * and otherwise fills the viewport — a screen-width decision derived from the
 * measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Container component for the main invitations screen.
 * Fetches and displays sent invitations with pagination support.
 */
const MainContainer = () => {
  const { data, fetchMore, loading, refetch, networkStatus } =
    useInvitationsListMyOrganiationsSentInvitationsQuery({
      variables: { status: InvitationStatus.Pending },
    });

  const isRefreshing = useMemo(
    () => networkStatus === NetworkStatus.refetch,
    [networkStatus]
  );

  const invitations = useMemo(
    () =>
      data?.listMyOrganizationsSentInvitations?.edges.map(edge => edge.node) ??
      [],
    [data?.listMyOrganizationsSentInvitations?.edges]
  );
  const endCursor = useMemo(
    () =>
      data?.listMyOrganizationsSentInvitations?.pageInfo.hasNextPage
        ? data?.listMyOrganizationsSentInvitations?.pageInfo.endCursor
        : null,
    [
      data?.listMyOrganizationsSentInvitations?.pageInfo.endCursor,
      data?.listMyOrganizationsSentInvitations?.pageInfo.hasNextPage,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const renderItem: ListRenderItem<InvitationsInvitationFragment> = useCallback(
    ({ item }) => {
      return <InvitationItem item={item} />;
    },
    []
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? ifWeb(640, 750) : width;

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && invitations.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, invitations.length]
  );

  useSetPageTitle("Invitations");

  return (
    <MainView
      invitations={invitations}
      refreshing={isRefreshing}
      widthContent={widthContent}
      loading={loading}
      renderItem={renderItem}
      renderListFooterComponent={renderListFooterComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
    />
  );
};

export default MainContainer;
